/* eslint-disable no-unused-vars -- uncomment when accounts are needed. */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { processApiOptions } from '../../hooks/use-apioptions';
import {
  clearLandingPageFilters,
  storeFilterApiResponses,
  updateLandingPageFilters,
} from '../../store/actions/landingPageFiltersAction';
import {
  getAssetTypeApi,
  getPlatformTypeApi,
  getRoleTypeApi,
} from '../../utilities/api/getDataFromApi';

const LandingPageFilterService = (
  setIsSidePanelOpen,
  setCurrentListItem,
  hasSelectedRoles
) => {
  // Get saved filters and API responses from Redux store
  const { selectedFilters, apiResponses } = useSelector(
    (state) =>
      state.landingPageFilters || { selectedFilters: {}, apiResponses: {} }
  );
  const savedPlatforms = selectedFilters?.checkedPlatforms || [];
  const savedAssets = selectedFilters?.checkedAssets || [];
  const savedRoles = selectedFilters?.checkedRoles || [];

  // Get cached API responses
  const cachedPlatformResponse = apiResponses?.platformResponse;
  const cachedAssetResponse = apiResponses?.assetResponse;
  const cachedRoleResponse = apiResponses?.roleResponse;

  // Platform
  const [platformTypeResponse, setPlatformTypeResponse] = useState(null);
  const [platformTypeOptions, setPlatformTypeOptions] = useState([]);
  const [checkedPlatforms, setCheckedPlatforms] = useState(savedPlatforms);
  // Asset Type
  const [assetTypeResponse, setAssetTypeResponse] = useState(null);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [checkedAssets, setCheckedAssets] = useState(savedAssets);
  // Account
  const [accountOptions, setAccountOptions] = useState([]);
  // Role Type (currently only one: 'Emergency Access')
  const [roleTypeResponse, setRoleTypeResponse] = useState(null);
  const [roleTypeOptions, setRoleTypeOptions] = useState([]);
  const [checkedRoles, setCheckedRoles] = useState(savedRoles);

  // Badge/Clear Button
  const [isFilterChecked, setIsFilterChecked] = useState(
    savedPlatforms.length > 0 || savedAssets.length > 0 || savedRoles.length > 0
  );

  const dispatch = useDispatch();

  // Fetch API data on mount if not already cached
  useEffect(() => {
    // Use cached responses if available
    if (cachedPlatformResponse && cachedAssetResponse && cachedRoleResponse) {
      setPlatformTypeResponse(cachedPlatformResponse);
      setAssetTypeResponse(cachedAssetResponse);
      setRoleTypeResponse(cachedRoleResponse);
    } else {
      const fetchData = async () => {
        try {
          const [platformResponse, assetResponse, roleResponse] =
            await Promise.all([
              getPlatformTypeApi(),
              getAssetTypeApi(),
              getRoleTypeApi(),
            ]);

          // Store responses in component state
          setPlatformTypeResponse(platformResponse);
          setAssetTypeResponse(assetResponse);
          setRoleTypeResponse(roleResponse);

          // Store responses in Redux for future use
          dispatch(
            storeFilterApiResponses({
              platformResponse,
              assetResponse,
              roleResponse,
            })
          );
        } catch (error) {
          /* silenced */
        }
      };

      fetchData();
    }
  }, [
    cachedPlatformResponse,
    cachedAssetResponse,
    cachedRoleResponse,
    dispatch,
  ]);

  // Process API responses
  useEffect(() => {
    if (platformTypeResponse) {
      processApiOptions(
        platformTypeResponse,
        setPlatformTypeOptions,
        savedPlatforms
      );
    }
  }, [platformTypeResponse, savedPlatforms]);

  useEffect(() => {
    if (assetTypeResponse) {
      processApiOptions(assetTypeResponse, setAssetTypeOptions, savedAssets);
    }
  }, [assetTypeResponse, savedAssets]);

  useEffect(() => {
    if (roleTypeResponse) {
      processApiOptions(roleTypeResponse, setRoleTypeOptions, savedRoles);
    }
  }, [roleTypeResponse, savedRoles]);

  // Update checked items when options change
  useEffect(() => {
    const getCheckedPlatforms = platformTypeOptions
      .filter((item) => item.isChecked)
      .map((item) => item.value);

    const getCheckedAssets = assetTypeOptions
      .filter((item) => item.isChecked)
      .map((item) => item.value);

    const getCheckedRoles = roleTypeOptions
      .filter((item) => item.isChecked)
      .map((item) => item.value);

    setCheckedPlatforms(getCheckedPlatforms);
    setCheckedAssets(getCheckedAssets);
    setCheckedRoles(getCheckedRoles);
  }, [platformTypeOptions, assetTypeOptions, roleTypeOptions]);

  // Update filter badge status
  useEffect(() => {
    const hasCheckedFilters =
      platformTypeOptions.some((option) => option.isChecked) ||
      assetTypeOptions.some((option) => option.isChecked) ||
      accountOptions.some((option) => option.isChecked) ||
      (roleTypeOptions.some((option) => option.isChecked) && !hasSelectedRoles);

    setIsFilterChecked(hasCheckedFilters);
  }, [
    platformTypeOptions,
    assetTypeOptions,
    accountOptions,
    roleTypeOptions,
    hasSelectedRoles,
  ]);

  // Handle filter selection
  const handleSelectedOptions = useCallback(
    (selectedOptions, options, setOptions) => {
      const updatedOptions = options.map((option) => ({
        ...option,
        isChecked: selectedOptions.some(
          (selOption) => selOption.value === option.value
        ),
      }));

      setOptions(updatedOptions);
      setIsSidePanelOpen(false);
      setCurrentListItem(null);

      // Determine which filter type is being updated
      const updatedFilters = {
        checkedPlatforms,
        checkedAssets,
        checkedRoles,
      };

      const selectedValues = selectedOptions.map((option) => option.value);

      if (options === platformTypeOptions) {
        updatedFilters.checkedPlatforms = selectedValues;
      } else if (options === assetTypeOptions) {
        updatedFilters.checkedAssets = selectedValues;
      } else if (options === roleTypeOptions) {
        updatedFilters.checkedRoles = selectedValues;
      }

      dispatch(updateLandingPageFilters(updatedFilters));
    },
    [
      checkedPlatforms,
      checkedAssets,
      checkedRoles,
      dispatch,
      platformTypeOptions,
      assetTypeOptions,
      roleTypeOptions,
      setIsSidePanelOpen,
      setCurrentListItem,
    ]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    // Create a reusable function to clear options
    const clearOptions = (options) =>
      options.map((option) => ({ ...option, isChecked: false }));

    // Clear UI state
    setPlatformTypeOptions(clearOptions(platformTypeOptions));
    setAssetTypeOptions(clearOptions(assetTypeOptions));
    setAccountOptions(clearOptions(accountOptions));

    // Handle role options based on hasSelectedRoles
    if (!hasSelectedRoles) {
      setRoleTypeOptions(clearOptions(roleTypeOptions));
      dispatch(clearLandingPageFilters());
    } else {
      // Preserve role filters in bulk role select mode
      dispatch(
        updateLandingPageFilters({
          checkedPlatforms: [],
          checkedAssets: [],
          checkedRoles,
        })
      );
    }

    setIsFilterChecked(hasSelectedRoles);
    setIsSidePanelOpen(false);
    setCurrentListItem(null);
  }, [
    platformTypeOptions,
    assetTypeOptions,
    accountOptions,
    roleTypeOptions,
    hasSelectedRoles,
    checkedRoles,
    dispatch,
    setIsSidePanelOpen,
    setCurrentListItem,
  ]);

  return {
    platform: {
      options: platformTypeOptions,
      setSelected: (selectedOptions) =>
        handleSelectedOptions(
          selectedOptions,
          platformTypeOptions,
          setPlatformTypeOptions
        ),
    },
    asset: {
      options: assetTypeOptions,
      setSelected: (selectedOptions) =>
        handleSelectedOptions(
          selectedOptions,
          assetTypeOptions,
          setAssetTypeOptions
        ),
    },
    account: {
      options: accountOptions,
      setSelected: (selectedOptions) =>
        handleSelectedOptions(
          selectedOptions,
          accountOptions,
          setAccountOptions
        ),
    },
    role: {
      options: roleTypeOptions,
      setSelected: (selectedOptions) =>
        handleSelectedOptions(
          selectedOptions,
          roleTypeOptions,
          setRoleTypeOptions
        ),
    },
    isFilterChecked,
    clearAllFilters,
    checkedPlatforms,
    checkedAssets,
    checkedRoles,
  };
};

export default LandingPageFilterService;
